import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Ship, Calendar, Users, MessageSquare, DollarSign, Shield, MapPin, Smartphone, UserCheck, Compass, FileText, Anchor, Settings, Wrench, Lock, Bell, FolderOpen, PenTool, UtensilsCrossed, FileWarning, BarChart3, LayoutGrid, Handshake, Eye, ClipboardCheck, Megaphone, Brain, UserCog, Lightbulb, Layers, Zap, ShieldCheck } from "lucide-react";
import { type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "@/components/LocalizedLink";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLocalePath } from "@/hooks/useLocalePath";
import { ROUTES, featurePath, solutionPath, whyUsPath } from "@/lib/routes";
import logo from "@/assets/logo.png?format=webp&quality=75&w=320";

interface NavItem { titleKey: string; href: string; icon: LucideIcon }

const charterFeatures: NavItem[] = [
  { titleKey: "features.fleet", href: featurePath("fleet"), icon: Ship },
  { titleKey: "features.charter", href: featurePath("charter"), icon: Calendar },
  { titleKey: "features.crm", href: featurePath("crm"), icon: Users },
  { titleKey: "features.crewClients", href: featurePath("crew-clients"), icon: UserCheck },
  { titleKey: "features.captainsBridge", href: featurePath("captains-bridge"), icon: Smartphone },
  { titleKey: "features.communications", href: featurePath("communications"), icon: MessageSquare },
  { titleKey: "features.finance", href: featurePath("finance"), icon: DollarSign },
  { titleKey: "features.compliance", href: featurePath("compliance"), icon: Shield },
  { titleKey: "features.ais", href: featurePath("ais"), icon: MapPin },
  { titleKey: "features.ownerPortal", href: featurePath("owner-portal"), icon: Compass },
  { titleKey: "features.documents", href: featurePath("documents"), icon: FileText },
  { titleKey: "features.security", href: featurePath("security"), icon: Lock },
];

const managementFeatures: NavItem[] = [
  { titleKey: "mgmtFeatures.fleet", href: featurePath("mgmt-fleet"), icon: Ship },
  { titleKey: "mgmtFeatures.crew", href: featurePath("mgmt-crew"), icon: Users },
  { titleKey: "mgmtFeatures.owners", href: featurePath("mgmt-owners"), icon: UserCheck },
  { titleKey: "mgmtFeatures.pms", href: featurePath("mgmt-pms"), icon: Wrench },
  { titleKey: "mgmtFeatures.provisioning", href: featurePath("mgmt-provisioning"), icon: UtensilsCrossed },
  { titleKey: "mgmtFeatures.tasks", href: featurePath("mgmt-tasks"), icon: Bell },
  { titleKey: "mgmtFeatures.comms", href: featurePath("mgmt-comms"), icon: MessageSquare },
  { titleKey: "mgmtFeatures.finance", href: featurePath("mgmt-finance"), icon: DollarSign },
  { titleKey: "mgmtFeatures.compliance", href: featurePath("mgmt-compliance"), icon: Shield },
  { titleKey: "mgmtFeatures.claims", href: featurePath("mgmt-claims"), icon: FileWarning },
  { titleKey: "mgmtFeatures.docs", href: featurePath("mgmt-docs"), icon: FolderOpen },
  { titleKey: "mgmtFeatures.signatures", href: featurePath("mgmt-signatures"), icon: PenTool },
  { titleKey: "mgmtFeatures.reporting", href: featurePath("mgmt-reporting"), icon: BarChart3 },
  { titleKey: "mgmtFeatures.security", href: featurePath("security"), icon: Lock },
];

const salesFeatures: NavItem[] = [
  { titleKey: "salesFeatures.listings", href: featurePath("sales-listings"), icon: LayoutGrid },
  { titleKey: "salesFeatures.sellerCrm", href: featurePath("sales-seller-crm"), icon: Users },
  { titleKey: "salesFeatures.buyerCrm", href: featurePath("sales-buyer-crm"), icon: UserCheck },
  { titleKey: "salesFeatures.deals", href: featurePath("sales-deals"), icon: Handshake },
  { titleKey: "salesFeatures.showings", href: featurePath("sales-showings"), icon: Eye },
  { titleKey: "salesFeatures.survey", href: featurePath("sales-survey"), icon: ClipboardCheck },
  { titleKey: "salesFeatures.marketing", href: featurePath("sales-marketing"), icon: Megaphone },
  { titleKey: "salesFeatures.marketIntel", href: featurePath("sales-market-intel"), icon: Brain },
  { titleKey: "salesFeatures.comms", href: featurePath("sales-comms"), icon: MessageSquare },
  { titleKey: "salesFeatures.finance", href: featurePath("sales-finance"), icon: DollarSign },
  { titleKey: "salesFeatures.compliance", href: featurePath("sales-compliance"), icon: Shield },
  { titleKey: "salesFeatures.ownerPortal", href: featurePath("sales-owner-portal"), icon: UserCog },
  { titleKey: "salesFeatures.reporting", href: featurePath("sales-reporting"), icon: BarChart3 },
  { titleKey: "salesFeatures.security", href: featurePath("sales-security"), icon: Lock },
];

type DropdownKey = "products" | "solutions" | "whyus" | null;

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [charterExpanded, setCharterExpanded] = useState(false);
  const [managementExpanded, setManagementExpanded] = useState(false);
  const [salesExpanded, setSalesExpanded] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { localePath } = useLocalePath();

  const isActive = (path: string) => {
    const parts = location.pathname.split("/").filter(Boolean);
    const pathWithoutLang = "/" + parts.slice(1).join("/");
    return pathWithoutLang === path || pathWithoutLang.startsWith(path + "/");
  };

  const solutionItems: NavItem[] = [
    { titleKey: "nav.solutionCharterBrokers", href: solutionPath("charter-brokers"), icon: Anchor },
    { titleKey: "nav.solutionYachtBrokers", href: solutionPath("yacht-brokers"), icon: Handshake },
    { titleKey: "nav.solutionYachtManagers", href: solutionPath("yacht-managers"), icon: Settings },
  ];

  const whyUsItems: NavItem[] = [
    { titleKey: "nav.whyUsAllInOne", href: whyUsPath("all-in-one"), icon: Layers },
    { titleKey: "nav.whyUsProfessionals", href: whyUsPath("built-for-professionals"), icon: Zap },
    { titleKey: "nav.whyUsSecurity", href: whyUsPath("security"), icon: ShieldCheck },
    { titleKey: "nav.whyUsMobile", href: whyUsPath("mobile"), icon: Smartphone },
  ];

  const simpleNavLinks = [
    { title: t("nav.services"), href: ROUTES.services },
    { title: t("nav.about"), href: ROUTES.about },
    { title: t("nav.careers"), href: ROUTES.careers },
    { title: t("nav.contact"), href: ROUTES.contact },
    { title: t("nav.news"), href: ROUTES.news },
  ];

  /* ── Desktop Products Mega-Menu ── */
  const DesktopProductsDropdown = () => (
    <div
      className="relative"
      onMouseEnter={() => setOpenDropdown("products")}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <button
        className={cn(
          "flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap",
          isActive(ROUTES.features) || isActive(ROUTES.charter) || isActive(ROUTES.management) || isActive(ROUTES.sales) ? "text-teal-glow" : "text-white/70 hover:text-white"
        )}
        aria-expanded={openDropdown === "products"}
        aria-haspopup="true"
      >
        {t("nav.products")} <ChevronDown className={cn("h-4 w-4 transition-transform", openDropdown === "products" && "rotate-180")} />
      </button>
      {openDropdown === "products" && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
          <div className="rounded-xl border border-white/10 bg-[hsl(220,28%,8%)] backdrop-blur-xl p-4 shadow-2xl w-[580px] max-h-[80vh] overflow-y-auto">
            <LocalizedLink to={ROUTES.features} className="px-3 py-2 text-sm font-semibold text-teal-glow hover:bg-white/5 rounded-md block mb-2">
              {t("nav.productsOverview")} →
            </LocalizedLink>

            {/* Charter */}
            <div className="mb-3">
              <LocalizedLink to={ROUTES.charter} className="flex items-center gap-2.5 px-3 py-2 text-sm font-semibold text-white hover:bg-white/5 rounded-md transition-colors">
                <Anchor className="h-4 w-4 text-teal-glow" />
                {t("nav.charter")}
              </LocalizedLink>
              <div className="grid grid-cols-2 gap-0.5 pl-3 mt-1">
                {charterFeatures.map((link) => (
                  <LocalizedLink
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/50 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  >
                    <link.icon className="h-3.5 w-3.5 text-teal-glow/60" />
                    {t(link.titleKey)}
                  </LocalizedLink>
                ))}
              </div>
            </div>

            {/* Management */}
            <div className="mb-3">
              <LocalizedLink to={ROUTES.management} className="flex items-center gap-2.5 px-3 py-2 text-sm font-semibold text-white hover:bg-white/5 rounded-md transition-colors">
                <Settings className="h-4 w-4 text-teal-glow" />
                {t("nav.management")}
              </LocalizedLink>
              <div className="grid grid-cols-2 gap-0.5 pl-3 mt-1">
                {managementFeatures.map((link) => (
                  <LocalizedLink
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/50 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  >
                    <link.icon className="h-3.5 w-3.5 text-teal-glow/60" />
                    {t(link.titleKey)}
                  </LocalizedLink>
                ))}
              </div>
            </div>

            {/* Sales */}
            <div className="mb-3">
              <LocalizedLink to={ROUTES.sales} className="flex items-center gap-2.5 px-3 py-2 text-sm font-semibold text-white hover:bg-white/5 rounded-md transition-colors">
                <Wrench className="h-4 w-4 text-teal-glow" />
                {t("nav.sales")}
              </LocalizedLink>
              <div className="grid grid-cols-2 gap-0.5 pl-3 mt-1">
                {salesFeatures.map((link) => (
                  <LocalizedLink
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/50 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  >
                    <link.icon className="h-3.5 w-3.5 text-teal-glow/60" />
                    {t(link.titleKey)}
                  </LocalizedLink>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ── Desktop Dropdown ── */
  const DesktopDropdown = ({
    label, dropdownKey, overviewHref, overviewLabel, links, columns = 2,
  }: {
    label: string; dropdownKey: DropdownKey; overviewHref: string; overviewLabel: string; links: NavItem[]; columns?: number;
  }) => (
    <div
      className="relative"
      onMouseEnter={() => setOpenDropdown(dropdownKey)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <button
        className={cn(
          "flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap",
          isActive(overviewHref) ? "text-teal-glow" : "text-white/70 hover:text-white"
        )}
        aria-expanded={openDropdown === dropdownKey}
        aria-haspopup="true"
      >
        {label} <ChevronDown className={cn("h-4 w-4 transition-transform", openDropdown === dropdownKey && "rotate-180")} />
      </button>
      {openDropdown === dropdownKey && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
          <div className={cn(
            "rounded-xl border border-white/10 bg-[hsl(220,28%,8%)] backdrop-blur-xl p-4 shadow-2xl",
            columns === 2 ? "w-[520px] grid grid-cols-2 gap-1" : "w-[300px] flex flex-col gap-1"
          )}>
            <LocalizedLink
              to={overviewHref}
              className={cn(
                "px-3 py-2 text-sm font-semibold text-teal-glow hover:bg-white/5 rounded-md mb-1",
                columns === 2 && "col-span-2"
              )}
            >
              {overviewLabel} →
            </LocalizedLink>
            {links.map((link) => (
              <LocalizedLink
                key={link.href}
                to={link.href}
                className="flex items-center gap-2.5 px-3 py-2 text-sm text-white/60 hover:text-white hover:bg-white/5 rounded-md transition-colors"
              >
                <link.icon className="h-4 w-4 text-teal-glow/70" />
                {t(link.titleKey)}
              </LocalizedLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  /* ── Mobile Products Section ── */
  const MobileProductsSection = () => (
    <>
      <button
        className="flex items-center justify-between w-full py-3 text-sm font-medium text-white/70 hover:text-white"
        onClick={() => setMobileExpanded(mobileExpanded === "products" ? null : "products")}
      >
        {t("nav.products")}
        <ChevronDown className={cn("h-4 w-4 transition-transform", mobileExpanded === "products" && "rotate-180")} />
      </button>
      {mobileExpanded === "products" && (
        <>
          <LocalizedLink to={ROUTES.features} className="block py-2 pl-4 text-sm font-semibold text-teal-glow" onClick={() => setMobileOpen(false)}>
            {t("nav.productsOverview")} →
          </LocalizedLink>
          {/* Charter */}
          <button
            className="flex items-center justify-between w-full py-2 pl-4 text-sm font-semibold text-white hover:text-teal-glow"
            onClick={() => setCharterExpanded(!charterExpanded)}
          >
            <span className="flex items-center gap-2">
              <Anchor className="h-3.5 w-3.5 text-teal-glow" />
              {t("nav.charter")}
            </span>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", charterExpanded && "rotate-180")} />
          </button>
          {charterExpanded && (
            <>
              {charterFeatures.map((link) => (
                <LocalizedLink key={link.href} to={link.href} className="flex items-center gap-2 py-2.5 pl-8 text-sm text-white/50 hover:text-white active:text-teal-glow" onClick={() => setMobileOpen(false)}>
                  <link.icon className="h-3 w-3 text-teal-glow/60" />
                  {t(link.titleKey)}
                </LocalizedLink>
              ))}
            </>
          )}
          {/* Management */}
          <button
            className="flex items-center justify-between w-full py-2 pl-4 text-sm font-semibold text-white hover:text-teal-glow"
            onClick={() => setManagementExpanded(!managementExpanded)}
          >
            <span className="flex items-center gap-2">
              <Settings className="h-3.5 w-3.5 text-teal-glow" />
              {t("nav.management")}
            </span>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", managementExpanded && "rotate-180")} />
          </button>
          {managementExpanded && (
            <>
              {managementFeatures.map((link) => (
                <LocalizedLink key={link.href} to={link.href} className="flex items-center gap-2 py-2.5 pl-8 text-sm text-white/50 hover:text-white active:text-teal-glow" onClick={() => setMobileOpen(false)}>
                  <link.icon className="h-3 w-3 text-teal-glow/60" />
                  {t(link.titleKey)}
                </LocalizedLink>
              ))}
            </>
          )}
          {/* Sales */}
          <button
            className="flex items-center justify-between w-full py-2 pl-4 text-sm font-semibold text-white hover:text-teal-glow"
            onClick={() => setSalesExpanded(!salesExpanded)}
          >
            <span className="flex items-center gap-2">
              <Wrench className="h-3.5 w-3.5 text-teal-glow" />
              {t("nav.sales")}
            </span>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", salesExpanded && "rotate-180")} />
          </button>
          {salesExpanded && (
            <>
              <LocalizedLink to={ROUTES.sales} className="block py-1.5 pl-8 text-xs text-teal-glow" onClick={() => setMobileOpen(false)}>
                {t("nav.sales")} →
              </LocalizedLink>
              {salesFeatures.map((link) => (
                <LocalizedLink key={link.href} to={link.href} className="flex items-center gap-2 py-2.5 pl-8 text-sm text-white/50 hover:text-white active:text-teal-glow" onClick={() => setMobileOpen(false)}>
                  <link.icon className="h-3 w-3 text-teal-glow/60" />
                  {t(link.titleKey)}
                </LocalizedLink>
              ))}
            </>
          )}
        </>
      )}
    </>
  );

  /* ── Mobile Section ── */
  const MobileSection = ({
    label, dropdownKey, overviewHref, overviewLabel, links,
  }: {
    label: string; dropdownKey: string; overviewHref: string; overviewLabel: string; links: NavItem[];
  }) => (
    <>
      <button
        className="flex items-center justify-between w-full py-3 text-sm font-medium text-white/70 hover:text-white"
        onClick={() => setMobileExpanded(mobileExpanded === dropdownKey ? null : dropdownKey)}
      >
        {label}
        <ChevronDown className={cn("h-4 w-4 transition-transform", mobileExpanded === dropdownKey && "rotate-180")} />
      </button>
      {mobileExpanded === dropdownKey && (
        <>
          <LocalizedLink to={overviewHref} className="block py-2 pl-4 text-sm font-semibold text-teal-glow" onClick={() => setMobileOpen(false)}>
            {overviewLabel} →
          </LocalizedLink>
          {links.map((link) => (
            <LocalizedLink key={link.href} to={link.href} className="flex items-center gap-2 py-2.5 pl-4 text-sm text-white/50 hover:text-white active:text-teal-glow" onClick={() => setMobileOpen(false)}>
              <link.icon className="h-3.5 w-3.5 text-teal-glow/60" />
              {t(link.titleKey)}
            </LocalizedLink>
          ))}
        </>
      )}
    </>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <LocalizedLink to="/" className="flex items-center gap-2 group">
          <img src={logo} alt="Seaventis" className="h-7 w-auto" width={160} height={18} />
        </LocalizedLink>

        <div className="hidden xl:flex items-center gap-0.5 flex-nowrap">
          <DesktopDropdown label={t("nav.solutions")} dropdownKey="solutions" overviewHref={ROUTES.solutions} overviewLabel={t("nav.solutionsOverview")} links={solutionItems} columns={1} />
          <DesktopProductsDropdown />
          {simpleNavLinks.slice(0, 1).map((link) => (
            <LocalizedLink key={link.href} to={link.href} className={cn("px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap", isActive(link.href) ? "text-teal-glow" : "text-white/70 hover:text-white")}>{link.title}</LocalizedLink>
          ))}
          <DesktopDropdown label={t("nav.whyUs")} dropdownKey="whyus" overviewHref={ROUTES.whyUs} overviewLabel={t("nav.whyUsOverview")} links={whyUsItems} columns={1} />
          {simpleNavLinks.slice(1).map((link) => (
            <LocalizedLink key={link.href} to={link.href} className={cn("px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap", isActive(link.href) ? "text-teal-glow" : "text-white/70 hover:text-white")}>{link.title}</LocalizedLink>
          ))}
        </div>

        <div className="flex items-center gap-2 xl:gap-3">
          <div className="hidden xl:block">
            <LanguageSwitcher variant="compact" />
          </div>
          <Button asChild className="hidden xl:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-semibold hover:shadow-[0_0_20px_-4px_hsl(38_80%_52%/0.3)]">
            <LocalizedLink to={ROUTES.demo}>{t("nav.bookDemo")}</LocalizedLink>
          </Button>
          <button className="xl:hidden text-white p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen}>
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden bg-[hsl(220,28%,8%)] border-t border-white/10 px-4 pb-6 pt-2 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
          <MobileSection label={t("nav.solutions")} dropdownKey="solutions" overviewHref={ROUTES.solutions} overviewLabel={t("nav.solutionsOverview")} links={solutionItems} />
          <MobileProductsSection />
          {simpleNavLinks.slice(0, 1).map((link) => (
            <LocalizedLink key={link.href} to={link.href} className="block py-3 text-sm font-medium text-white/70 hover:text-white" onClick={() => setMobileOpen(false)}>{link.title}</LocalizedLink>
          ))}
          <MobileSection label={t("nav.whyUs")} dropdownKey="whyus" overviewHref={ROUTES.whyUs} overviewLabel={t("nav.whyUsOverview")} links={whyUsItems} />
          {simpleNavLinks.slice(1).map((link) => (
            <LocalizedLink key={link.href} to={link.href} className="block py-3 text-sm font-medium text-white/70 hover:text-white" onClick={() => setMobileOpen(false)}>{link.title}</LocalizedLink>
          ))}
          <div className="pt-4 border-t border-white/10 mt-4">
            <LanguageSwitcher variant="compact" />
          </div>
          <Button asChild className="w-full mt-3 bg-primary text-primary-foreground font-semibold">
            <LocalizedLink to={ROUTES.demo} onClick={() => setMobileOpen(false)}>{t("nav.bookDemo")}</LocalizedLink>
          </Button>
        </div>
      )}
    </nav>
  );
}
